import { Component } from '@theme/component';
import { computeRuptureButtonState } from '@theme/rupture-button-state';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * @typedef {Object} AxComparateurCardRefs
 * @property {HTMLDetailsElement[]} selects - Custom dropdown <details> wrappers.
 * @property {HTMLElement[]} triggerLabels - Span showing selected option name (per dropdown).
 * @property {HTMLElement[]} [triggerSwatches] - Swatch indicator in the trigger (per dropdown that has swatches).
 * @property {HTMLInputElement} variantId - Hidden input with current variant ID.
 * @property {HTMLButtonElement} addButton - Submit button.
 * @property {HTMLElement} price - Price display element.
 * @property {HTMLElement} ctaLabel - CTA label element.
 * @property {HTMLElement} [prixShaker] - Prix/shaker value cell.
 * @property {HTMLElement} [prixShakerRow] - Prix/shaker row container (toggled).
 */

/**
 * @typedef {Object} Variant
 * @property {number} id
 * @property {boolean} available
 * @property {string} price
 * @property {string[]} options
 */

/**
 * Manages variant selection for the comparateur card with custom <details> dropdowns.
 *
 * On radio change : finds the matching variant, updates form/price/availability,
 * updates the dropdown's trigger label + swatch color, and closes the dropdown.
 *
 * @extends {Component<AxComparateurCardRefs>}
 */
class AxComparateurCardComponent extends Component {
  /** @type {Variant[]} */
  #variants = [];

  /** @type {Record<string, { oos: boolean, def: boolean, temp: boolean }>} */
  #ruptureFlags = {};

  /** @type {Record<string, number>} */
  #variantDoses = {};

  /** @type {((e: Event) => void) | null} */
  #outsideHandler = null;

  connectedCallback() {
    super.connectedCallback();
    try {
      this.#variants = JSON.parse(this.dataset.variants || '[]');
    } catch {
      this.#variants = [];
    }
    try {
      this.#ruptureFlags = JSON.parse(this.dataset.variantRupture || '{}');
    } catch {
      this.#ruptureFlags = {};
    }
    try {
      this.#variantDoses = JSON.parse(this.dataset.variantDoses || '{}');
    } catch {
      this.#variantDoses = {};
    }
    this.#updateAvailability();
    this.#promoteSelected();
    this.#bindOutsideClose();
    this.#bindDropdownSizing();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unbindOutsideClose();
  }

  // La valeur cochée remonte en tête de sa liste (demande client) : sur une longue
  // liste de saveurs, elle pouvait se trouver hors du champ visible à l'ouverture.
  // Déplacement du noeud plutôt qu'un `order` CSS : l'ordre de navigation clavier
  // du groupe de radios suit le DOM, il resterait sinon désaccordé de l'affichage.
  #promoteSelected() {
    for (const details of this.refs.selects || []) {
      if (!(details instanceof HTMLDetailsElement)) continue;

      const list = details.querySelector('.ax-comparateur-card__select-list');
      const item = list?.querySelector('.ax-comparateur-card__select-item:has(input:checked)');
      if (list && item && item !== list.firstElementChild) list.prepend(item);
    }
  }

  // La liste partait sous le pli, son bas devenant inatteignable. Répartition des
  // rôles : le CSS fixe le plafond PAR DEVICE (cf. `--select-list-max-height`,
  // 18rem desktop / 13.75rem mobile), le JS ne fait que le RÉDUIRE quand le bas
  // de l'écran arrive avant — il ne l'augmente jamais. Un `max-height` purement
  // CSS ne peut pas le savoir : la position du trigger dépend du contenu de la carte.
  #bindDropdownSizing() {
    for (const details of this.refs.selects || []) {
      if (!(details instanceof HTMLDetailsElement)) continue;

      details.addEventListener('toggle', () => {
        const list = details.querySelector('.ax-comparateur-card__select-list');
        if (!(list instanceof HTMLElement)) return;

        list.style.maxHeight = '';
        if (!details.open) return;

        const trigger = details.querySelector('summary');
        if (!trigger) return;

        const cap = parseFloat(getComputedStyle(list).maxHeight) || Infinity;
        const available = window.innerHeight - trigger.getBoundingClientRect().bottom - 8;
        const target = Math.min(cap, available);

        // Plancher : en dessous la liste ne montrerait plus assez d'options pour
        // être utile — on la laisse alors déborder plutôt que de l'écraser.
        if (target >= 160 && target < cap) list.style.maxHeight = `${Math.floor(target)}px`;
      });
    }
  }

  /**
   * @param {Event} event
   */
  handleOptionChange(event) {
    const target = event?.target;
    if (!(target instanceof HTMLInputElement)) return;
    const optionIndex = parseInt(target.dataset.optionIndex || '', 10);
    if (Number.isNaN(optionIndex)) return;

    const newValue = target.value;
    this.#updateTrigger(optionIndex, newValue, target.dataset.swatchColor || '', target);
    this.#closeDropdown(optionIndex);

    const variant = this.#findCurrentVariant();
    this.#updateForm(variant);
    this.#updateAvailability();
    // Après la fermeture du menu : la liste est déjà réordonnée à la réouverture,
    // sans mouvement visible sous les yeux de l'utilisateur.
    this.#promoteSelected();
  }

  /**
   * Add the selected variant to the cart + refresh the cart drawer sections.
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    const form = event.target;
    const { addButton, variantId } = this.refs;
    if (!(form instanceof HTMLFormElement) || !variantId?.value || addButton?.disabled) return;
    if (addButton?.dataset.state === 'loading') return;

    addButton?.setAttribute('aria-busy', 'true');
    if (addButton) addButton.dataset.state = 'loading';

    const formData = new FormData(form);

    const sectionIds = [];
    for (const cartItems of document.querySelectorAll('cart-items-component')) {
      if (cartItems instanceof HTMLElement && cartItems.dataset.sectionId) {
        sectionIds.push(cartItems.dataset.sectionId);
      }
    }
    if (sectionIds.length) formData.set('sections', sectionIds.join(','));

    const config = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const result = await response.json();

      if (result.status) {
        this.dispatchEvent(new CartErrorEvent(this.id || 'ax-comparateur-card', result.message, result.description, result.errors));
        return;
      }

      this.dispatchEvent(
        new CartAddEvent(result, this.id || 'ax-comparateur-card', {
          source: 'product-form-component',
          // Quantité AJOUTÉE : cart-icon l'additionne au compteur — absente, il
          // ajoutait 0 et la bulle ne bougeait pas (ticket 12796152000).
          itemCount: Number(formData.get('quantity')) || 1,
          productId: this.dataset.productId,
          sections: result.sections,
        })
      );
    } catch (error) {
      console.error('ax-comparateur-card: add to cart failed', error);
    } finally {
      addButton?.removeAttribute('aria-busy');
      if (addButton) delete addButton.dataset.state;
    }
  }

  /**
   * Recompute each option value's availability based on the currently-selected
   * values of the OTHER options. Disables/greys out unavailable ones.
   */
  #updateAvailability() {
    const selected = this.#getSelectedValues();
    const radios = this.querySelectorAll('input[type="radio"]');

    for (const radio of radios) {
      if (!(radio instanceof HTMLInputElement)) continue;
      const optIdx = parseInt(radio.dataset.optionIndex || '', 10);
      if (Number.isNaN(optIdx)) continue;

      const candidate = selected.slice();
      candidate[optIdx] = radio.value;

      const matching = this.#variants.find((v) => this.#optionsEqual(v.options, candidate));
      const available = Boolean(matching?.available);

      radio.disabled = !available;
      radio.dataset.available = String(available);
      const li = radio.closest('.ax-comparateur-card__select-item');
      if (li instanceof HTMLElement) {
        li.classList.toggle('ax-comparateur-card__select-item--unavailable', !available);
      }
    }
  }

  #findCurrentVariant() {
    const selectedValues = this.#getSelectedValues();
    return this.#variants.find((v) => this.#optionsEqual(v.options, selectedValues)) || null;
  }

  /**
   * Collect selected values across radios AND hidden inputs (for single-value
   * options that don't render a dropdown).
   *
   * @returns {string[]}
   */
  #getSelectedValues() {
    /** @type {string[]} */
    const values = [];
    const radios = this.querySelectorAll('input[type="radio"]:checked');
    for (const input of radios) {
      if (input instanceof HTMLInputElement) {
        const idx = parseInt(input.dataset.optionIndex || '', 10);
        if (!Number.isNaN(idx)) values[idx] = input.value;
      }
    }
    const hiddens = this.querySelectorAll('input[type="hidden"][data-option-static="true"]');
    for (const input of hiddens) {
      if (input instanceof HTMLInputElement) {
        const idx = parseInt(input.dataset.optionIndex || '', 10);
        if (!Number.isNaN(idx)) values[idx] = input.value;
      }
    }
    return values;
  }

  /**
   * @param {string[]} a
   * @param {string[]} b
   */
  #optionsEqual(a, b) {
    if (!a || !b || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }

  /**
   * @param {number} optionIndex
   * @param {string} value
   * @param {string} swatchColor
   * @param {HTMLInputElement} [source] - Radio choisi, source des badges à recopier.
   */
  #updateTrigger(optionIndex, value, swatchColor, source) {
    const label = this.refs.triggerLabels?.[optionIndex];
    if (label) label.textContent = value;
    const swatch = this.refs.triggerSwatches?.[optionIndex];
    if (swatch && swatchColor) swatch.style.backgroundColor = swatchColor;
    this.#syncTriggerBadges(label, source);
  }

  // Recopie les badges (« Premium », « New ») de l'option choisie dans le trigger :
  // on clone le markup déjà rendu côté serveur plutôt que de le reconstruire,
  // pour rester aligné sur le Liquid (libellés traduits inclus).
  #syncTriggerBadges(label, source) {
    const holder = label?.parentElement;
    if (!holder || !source) return;

    for (const badge of holder.querySelectorAll('.ax-flavor-badge')) badge.remove();

    const option = source.closest('.ax-comparateur-card__select-option');
    if (!option) return;

    for (const badge of option.querySelectorAll('.ax-flavor-badge')) {
      holder.appendChild(badge.cloneNode(true));
    }
  }

  /**
   * @param {number} optionIndex
   */
  #closeDropdown(optionIndex) {
    const details = this.refs.selects?.[optionIndex];
    if (details instanceof HTMLDetailsElement) details.open = false;
  }

  /**
   * @param {Variant | null} variant
   */
  #updateForm(variant) {
    const { variantId, price, addButton, ctaLabel } = this.refs;

    if (!variant) {
      if (addButton) addButton.disabled = true;
      if (ctaLabel) ctaLabel.textContent = this.dataset.labelSoldOut || 'Épuisé';
      if (this.refs.prixShakerRow) this.refs.prixShakerRow.hidden = true;
      return;
    }

    if (variantId) variantId.value = String(variant.id);
    if (price) price.textContent = this.#formatMoney(variant.price);
    const flags = this.#ruptureFlags[String(variant.id)] || {};
    const state = computeRuptureButtonState(
      {
        available: variant.available,
        outOfStock: flags.oos,
        ruptureDefinitive: flags.def,
        ruptureTemporaire: flags.temp,
      },
      {
        addText: this.dataset.labelAddToCart,
        soldOutText: this.dataset.labelSoldOut,
        tempText: this.dataset.labelRuptureTemporaire,
        defText: this.dataset.labelRuptureDefinitive,
      }
    );
    if (addButton) addButton.disabled = state.disabled;
    if (ctaLabel) ctaLabel.textContent = state.text;

    const doses = this.#variantDoses[String(variant.id)] || 0;
    const { prixShaker, prixShakerRow } = this.refs;
    if (prixShaker && prixShakerRow) {
      if (doses > 0) {
        prixShaker.textContent = this.#formatMoney(Math.round(variant.price / doses));
        prixShakerRow.hidden = false;
      } else {
        prixShaker.textContent = '';
        prixShakerRow.hidden = true;
      }
    }
  }

  /**
   * Format aligned with Shopify's `| money` filter output (no space before €,
   * comma decimal separator).
   *
   * @param {string | number} priceCents
   */
  #formatMoney(priceCents) {
    const cents = typeof priceCents === 'number' ? priceCents : parseInt(priceCents, 10);
    if (Number.isNaN(cents)) return '';
    const amount = cents / 100;
    const currency = window.Shopify?.currency?.active || 'EUR';
    try {
      const formatted = new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
        style: 'currency',
        currency,
      }).format(amount);
      // Strip all non-breaking + regular spaces between number and currency symbol to match `| money` output.
      return formatted.replace(/\s+/g, '');
    } catch {
      return `${amount.toFixed(2).replace('.', ',')}€`;
    }
  }

  #bindOutsideClose() {
    this.#outsideHandler = (event) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      const dropdowns = this.refs.selects || [];
      for (const details of dropdowns) {
        if (details instanceof HTMLDetailsElement && details.open && !details.contains(target)) {
          details.open = false;
        }
      }
    };
    document.addEventListener('click', this.#outsideHandler);
  }

  #unbindOutsideClose() {
    if (this.#outsideHandler) {
      document.removeEventListener('click', this.#outsideHandler);
      this.#outsideHandler = null;
    }
  }
}

if (!customElements.get('ax-comparateur-card')) {
  customElements.define('ax-comparateur-card', AxComparateurCardComponent);
}
